import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { movieList } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import ShareDialog from '@/components/feature/ShareDialog';
import MovieCard from '@/pages/movies/components/MovieCard';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => movieList.find((m) => m.id === id), [id]);
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);

  usePageMeta({
    title: movie ? `${movie.title} - 靠逼` : '内容无法显示 - 靠逼',
    description: movie ? movie.intro[0] : '内容无法显示，请稍后重试。',
    keywords: movie ? `${movie.title},${movie.type},${movie.genre},靠逼` : '靠逼,影视推荐',
  });

  const related = useMemo(() => {
    if (!movie) return [];
    const sameType = movieList.filter((m) => m.type === movie.type && m.id !== movie.id);
    const pool = sameType.length >= 3 ? sameType : movieList.filter((m) => m.id !== movie.id);
    return pool.slice(0, 3);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容无法显示，请稍后重试"
          description="你访问的影视内容可能已被移除或链接有误。"
          actionLabel="返回影视推荐"
          actionTo="/movies"
        />
      </div>
    );
  }

  const favored = isFavorite(movie.id);

  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <Breadcrumb items={[{ label: '影视推荐', to: '/movies' }, { label: movie.title }]} />
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[300px_1fr]">
          <div className="mx-auto w-full max-w-[300px] md:mx-0">
            <div className="relative w-full overflow-hidden rounded-lg bg-background-100" style={{ aspectRatio: '3 / 4' }}>
              <img
                src={movie.cover}
                alt={movie.title}
                title={`${movie.title} - 靠逼影视推荐`}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggle(movie.id)}
                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  favored
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                <i className={favored ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                {favored ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-foreground-400">收藏状态保存在本地浏览器，仅作演示</p>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{movie.title}</h1>
              <span className="rounded-md bg-foreground-950/80 px-2.5 py-1 text-xs font-medium text-white">
                {movie.type}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4 text-sm sm:grid-cols-3">
              <div>
                <p className="text-xs text-foreground-400">上映年份</p>
                <p className="mt-0.5 text-foreground-900">{movie.year} 年</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">类型</p>
                <p className="mt-0.5 text-foreground-900">{movie.genre}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">地区</p>
                <p className="mt-0.5 text-foreground-900">{movie.region}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">导演 / 出品</p>
                <p className="mt-0.5 text-foreground-900">{movie.director}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-foreground-400">主演</p>
                <p className="mt-0.5 text-foreground-900">{movie.cast.join(' / ')}</p>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-700">推荐指数</span>
                <span className="flex items-baseline gap-1 font-heading text-2xl font-black text-primary-600">
                  {movie.rating.toFixed(1)}
                  <span className="text-xs font-medium text-foreground-400">/ 10</span>
                </span>
              </div>
              <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{ width: `${(movie.rating / 10) * 100}%` }}
                />
              </div>
              <p className="mt-2 text-[11px] text-foreground-400">推荐指数为可视化演示，并非真实评分数据</p>
            </div>

            <div className="mt-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <div className="mt-3 flex flex-col gap-3 text-[15px] leading-loose text-foreground-700">
                {movie.intro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="mb-5 flex items-center gap-2 border-b border-background-200 pb-3">
            <span className="flex h-7 w-1.5 rounded-full bg-accent-500" />
            <h2 className="font-heading text-xl font-black text-foreground-950">相关推荐</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={movie.title} />
    </div>
  );
}